import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { IFonteVinculadaSaldoDeContas } from '../../../../../interfaces';

export type FonteTagCellItem =
    | IFonteVinculadaSaldoDeContas
    | { id: number; numero: string; descricao?: string };

export interface IUseFontesTagsCellProps {
    fontes?: FonteTagCellItem[];
}

export const getFonteNumero = (item: FonteTagCellItem): string => {
    if ('fonte' in item) {
        return item.fonte?.numero || '';
    }
    return item.numero || '';
};

export const useFontesTagsCell = ({ fontes }: IUseFontesTagsCellProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState<number | null>(null);
    const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

    const todasAsFontes = useMemo(() => {
        return fontes?.map((e) => getFonteNumero(e)).filter(Boolean).join(', ') || '';
    }, [fontes]);

    useLayoutEffect(() => {
        const containerEl = containerRef.current;
        const measureEl = measureRef.current;
        if (!containerEl || !measureEl) return;

        let frameId: number | null = null;

        const calculateVisible = () => {
            const containerWidth = containerEl.clientWidth;
            if (containerWidth <= 0) return;

            measureEl.style.width = `${containerWidth}px`;

            const children = Array.from(measureEl.children) as HTMLElement[];
            if (children.length === 0) {
                setIsOverflowing(false);
                setVisibleCount(null);
                return;
            }

            const lines: HTMLElement[][] = [];
            for (let i = 0; i < children.length; i++) {
                const el = children[i];
                if (lines.length === 0) {
                    lines.push([el]);
                } else {
                    const currentLine = lines[lines.length - 1];
                    const firstElOnLine = currentLine[0];
                    if (el.offsetTop > firstElOnLine.offsetTop + 8) {
                        lines.push([el]);
                    } else {
                        currentLine.push(el);
                    }
                }
            }

            if (lines.length <= 3) {
                // Todas as tags cabem em até 3 linhas
                setIsOverflowing(false);
                setVisibleCount(children.length);
                return;
            }

            // Ultrapassa 3 linhas
            setIsOverflowing(true);

            // Largura necessária para a tag '...' (34px tag + 8px margem + 6px margem de segurança)
            const DOTS_REQUIRED_WIDTH = 48;
            const line1Count = lines[0]?.length || 0;
            const line2Count = lines[1]?.length || 0;
            const line3Elements = lines[2] || [];

            let line3Count = line3Elements.length;
            while (line3Count > 0) {
                const lastEl = line3Elements[line3Count - 1];
                if (lastEl.offsetLeft + lastEl.offsetWidth + DOTS_REQUIRED_WIDTH <= containerWidth) {
                    break;
                }
                line3Count--;
            }

            const totalCount = line1Count + line2Count + line3Count;
            setVisibleCount(Math.max(1, totalCount));
        };

        calculateVisible();

        frameId = requestAnimationFrame(() => {
            calculateVisible();
        });

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
                calculateVisible();
            });
            resizeObserver.observe(containerEl);
            return () => {
                if (frameId !== null) cancelAnimationFrame(frameId);
                resizeObserver.disconnect();
            };
        }

        return () => {
            if (frameId !== null) cancelAnimationFrame(frameId);
        };
    }, [fontes]);

    const tagsToRender = isOverflowing && visibleCount !== null && fontes
        ? fontes.slice(0, visibleCount)
        : fontes || [];

    const hasFontes = !!fontes && fontes.length > 0;

    return {
        containerRef,
        measureRef,
        tagsToRender,
        isOverflowing,
        todasAsFontes,
        hasFontes,
    };
};

export default useFontesTagsCell;
